import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../api/axiosInstance';
import { FaPaperclip, FaSmile, FaPaperPlane, FaComments, FaTimes, FaExternalLinkAlt, FaUserEdit, FaSignOutAlt } from 'react-icons/fa';
import '../css/FloatingChat.css';

// 작성자별 고유 파스텔 아바타 색상 생성기 (Guestbook과 일체감 형성)
const getAvatarColor = (name) => {
  const colors = [
    { bg: "#fee2e2", text: "#ef4444" },
    { bg: "#ffedd5", text: "#f97316" },
    { bg: "#fef9c3", text: "#eab308" },
    { bg: "#dcfce7", text: "#22c55e" },
    { bg: "#ecfdf5", text: "#10b981" },
    { bg: "#e0f2fe", text: "#0ea5e9" },
    { bg: "#dbeafe", text: "#3b82f6" },
    { bg: "#e0e7ff", text: "#6366f1" },
    { bg: "#f3e8ff", text: "#a855f7" },
    { bg: "#fae8ff", text: "#d946ef" },
    { bg: "#ffe4e6", text: "#f43f5e" }
  ];
  if (!name) return colors[7];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const FloatingChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [nickname, setNickname] = useState(() => sessionStorage.getItem('chat_nickname') || '');
  const [tempNickname, setTempNickname] = useState('');
  const [messageText, setMessageText] = useState('');
  const [messages, setMessages] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [activeUsers, setActiveUsers] = useState([]);
  const [showUserList, setShowUserList] = useState(false);
  const [isEditingNickname, setIsEditingNickname] = useState(false);
  const [newNickname, setNewNickname] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('connecting'); // 'connecting' | 'connected' | 'disconnected'

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // isOpen 상태를 Ref로 추적하여, 소켓 이벤트 리스너가 재등록 없이 최신 상태를 참조하도록 유도
  const isOpenRef = useRef(isOpen);
  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const RECOMMENDED_EMOJIS = ['😀', '😂', '😍', '👍', '🔥', '🎉', '🚀', '💬', '💡', '⚠️', '🚨', '🌀'];

  // WebSocket 연결 함수
  useEffect(() => {
    if (!nickname) return;

    // 프로토콜 및 호스트에 맞춤 동적 WebSocket URL 결정
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const wsUrl = `${protocol}//${host}/ws/chat`;

    const connectSocket = () => {
      console.log(`Connecting to Chat WebSocket: ${wsUrl}`);
      setConnectionStatus('connecting');
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      socket.onopen = () => {
        console.log('✅ Chat WebSocket Connected');
        setConnectionStatus('connected');
        
        // 닉네임 등록 요청 전송
        socket.send(JSON.stringify({ type: 'register', nickname }));

        // 입장 메시지 브로드캐스트
        const joinMsg = {
          type: 'system',
          text: `🔔 ${nickname}님이 입장하셨습니다.`
        };
        socket.send(JSON.stringify(joinMsg));
      };

      socket.onmessage = (event) => {
        try {
          const parsed = JSON.parse(event.data);
          
          // 실시간 활성 접속자 목록 갱신
          if (parsed.type === 'userList') {
            setActiveUsers(parsed.users);
            return;
          }
          
          setMessages((prev) => [...prev, parsed]);

          // 창이 닫혀 있을 때 메시지 수신 시 뱃지 카운트 증가 (Ref 사용으로 디펜던시 제외)
          if (!isOpenRef.current && parsed.type !== 'system') {
            setUnreadCount((c) => c + 1);
          }
        } catch (err) {
          console.error('Failed to parse socket message:', err);
        }
      };

      socket.onclose = (e) => {
        console.warn('❌ Chat WebSocket Closed. Attempting reconnect...', e.reason);
        setConnectionStatus('disconnected');
        setTimeout(() => {
          if (sessionStorage.getItem('chat_nickname')) {
            connectSocket();
          }
        }, 3000);
      };

      socket.onerror = (err) => {
        console.error('Chat WebSocket Error:', err);
        setConnectionStatus('disconnected');
      };
    };

    connectSocket();

    return () => {
      if (socketRef.current) {
        // 퇴장 메시지 발송 후 소켓 닫기
        const leaveMsg = {
          type: 'system',
          text: `🚪 ${nickname}님이 퇴장하셨습니다.`
        };
        if (socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify(leaveMsg));
        }
        // 의도적인 닫기이므로 onclose 재연결 루프가 돌지 않도록 리스너를 제거합니다.
        socketRef.current.onclose = null;
        socketRef.current.onerror = null;
        socketRef.current.close();
      }
    };
  }, [nickname]);

  // 새로운 메시지 수신 시 스크롤 최하단 이동
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // 채팅 활성화 시 뱃지 초기화
  const handleToggleChat = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  // 닉네임 최초 입장 처리
  const handleJoinChat = (e) => {
    e.preventDefault();
    if (!tempNickname.trim()) return;
    const cleanNick = tempNickname.trim();
    setNickname(cleanNick);
    sessionStorage.setItem('chat_nickname', cleanNick);
  };

  // 대화방 나가기 처리
  const handleLeaveChat = () => {
    if (!window.confirm('정말 대화방에서 나가시겠습니까? 대화 기록과 설정된 닉네임이 초기화됩니다.')) return;
    
    sessionStorage.removeItem('chat_nickname');
    setNickname('');
    setTempNickname('');
    setMessages([]);
    setShowUserList(false);
    setIsEditingNickname(false);
  };

  // 닉네임 변경 적용 처리
  const handleChangeNickname = (e) => {
    e.preventDefault();
    if (!newNickname.trim() || newNickname.trim() === nickname) {
      setIsEditingNickname(false);
      return;
    }
    const cleanNewNick = newNickname.trim();

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      // 1. 닉네임 변경 공지 브로드캐스트
      const announceMsg = {
        type: 'system',
        text: `📢 ${nickname}님이 ${cleanNewNick}님으로 닉네임을 변경하셨습니다.`
      };
      socketRef.current.send(JSON.stringify(announceMsg));

      // 2. 서버에 닉네임 업데이트 재등록
      socketRef.current.send(JSON.stringify({ type: 'register', nickname: cleanNewNick }));
      
      // 3. 로컬 상태 반영
      setNickname(cleanNewNick);
      sessionStorage.setItem('chat_nickname', cleanNewNick);
      setIsEditingNickname(false);
    } else {
      alert('채팅 서버와 연결이 끊겨 닉네임을 변경할 수 없습니다.');
    }
  };

  // 텍스트 메시지 발송
  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!messageText.trim()) return;

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      const chatMsg = {
        type: 'chat',
        id: 'msg_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
        sender: nickname,
        text: messageText,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      socketRef.current.send(JSON.stringify(chatMsg));
      setMessageText('');
      setShowEmojiPicker(false);
    } else {
      alert('채팅 서버와 연결되어 있지 않습니다. 인터넷 연결이나 Nginx 웹소켓 프록시 설정을 확인해 주세요.');
    }
  };

  // n8n 파일 업로드 연동 구현
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('attachment', file);

    setIsUploading(true);
    try {
      // n8n attachments 웹훅 파일 업로드 실행
      const response = await axiosInstance.post('attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      if (response.data && response.data.status === '0000') {
        const fileUrl = response.data.data.fileName;
        
        // 업로드된 파일 주소를 소켓을 통해 전송
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
           const fileMsg = {
            type: 'chat',
            id: 'msg_' + Date.now() + '_' + Math.random().toString(36).substring(2, 6),
            sender: nickname,
            text: '',
            fileUrl: fileUrl,
            fileName: file.name,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          socketRef.current.send(JSON.stringify(fileMsg));
        }
      } else {
        alert('파일 업로드에 실패했습니다. (응답 코드가 정상적이지 않습니다.)');
      }
    } catch (err) {
      console.error('File upload failed:', err);
      alert('업로드 처리 중 서버 에러가 발생했습니다.');
    } finally {
      setIsUploading(false);
      e.target.value = ''; // 동일 파일 재업로드 지원용 리셋
    }
  };

  // 이모티콘 팝오버 선택 시 입력창 반영
  const handleSelectEmoji = (emoji) => {
    setMessageText((prev) => prev + emoji);
    setShowEmojiPicker(false);
  };

  return (
    <>
      {/* 1. 우측 하단 플로팅 트리거 버튼 (FAB) */}
      <button className="floating-chat-toggle" onClick={handleToggleChat} title="실시간 익명 채팅">
        {isOpen ? <FaTimes /> : <FaComments />}
        {unreadCount > 0 && <span className="floating-chat-badge">{unreadCount}</span>}
      </button>

      {/* 2. 채팅 메인 윈도우 */}
      {isOpen && (
        <div className="floating-chat-window">
          {!nickname ? (
            /* 닉네임 설정 및 대화방 온보딩 */
            <div className="floating-chat-onboarding">
              <div className="onboarding-title">실시간 익명 대화방 💬</div>
              <p className="onboarding-desc">
                접속자들과 별도 가입 없이 소통할 수 있는 채팅방입니다. 대화에 사용할 닉네임을 설정해 주세요.
              </p>
              <form onSubmit={handleJoinChat} style={{ width: '100%' }}>
                <input
                  type="text"
                  maxLength={10}
                  className="onboarding-input"
                  placeholder="닉네임 입력 (최대 10자)"
                  value={tempNickname}
                  onChange={(e) => setTempNickname(e.target.value)}
                  autoFocus
                />
                <button type="submit" className="onboarding-btn">대화방 입장</button>
              </form>
            </div>
          ) : (
            /* 실시간 채팅 인터페이스 */
            <>
              {/* 헤더 */}
              <div className="chat-window-header">
                <div className="chat-header-title-box">
                  <div className="chat-header-title">
                    <span className={`chat-header-status status-${connectionStatus}`}></span>
                    <span>
                      {connectionStatus === 'connected' && '실시간 익명 채팅'}
                      {connectionStatus === 'connecting' && '채팅 서버 연결 중...'}
                      {connectionStatus === 'disconnected' && '채팅 연결 끊김'}
                    </span>
                  </div>
                  <span 
                    className="chat-header-subtitle"
                    onClick={() => {
                      if (connectionStatus === 'connected') {
                        setIsEditingNickname(false);
                        setShowUserList(!showUserList);
                      }
                    }}
                    style={{ cursor: connectionStatus === 'connected' ? 'pointer' : 'default', userSelect: 'none' }}
                  >
                    {connectionStatus === 'connected' ? `👥 현재 ${activeUsers.length || 1}명 참여 중 ${showUserList ? '▲' : '▼'}` : '서버 접속 상태를 확인하고 있습니다.'}
                  </span>
                </div>
                <div className="chat-header-actions">
                  {connectionStatus === 'connected' && (
                    <>
                      <button 
                        className="chat-header-btn" 
                        onClick={() => {
                          setNewNickname(nickname);
                          setShowUserList(false);
                          setIsEditingNickname(!isEditingNickname);
                        }} 
                        title="닉네임 변경"
                        style={{ color: isEditingNickname ? '#6366f1' : '#64748b' }}
                      >
                        <FaUserEdit size={14} />
                      </button>
                      <button 
                        className="chat-header-btn text-danger" 
                        onClick={handleLeaveChat} 
                        title="대화방 나가기"
                      >
                        <FaSignOutAlt size={14} />
                      </button>
                    </>
                  )}
                  <button className="chat-header-btn" onClick={() => setIsOpen(false)} title="최소화">
                    <FaTimes size={14} />
                  </button>
                </div>
              </div>

              {/* 닉네임 변경 패널 */}
              {isEditingNickname && connectionStatus === 'connected' && (
                <div className="chat-active-users-panel">
                  <div className="active-users-panel-header">닉네임 변경</div>
                  <form onSubmit={handleChangeNickname} className="d-flex gap-2 align-items-center mt-1">
                    <input
                      type="text"
                      maxLength={10}
                      className="onboarding-input mb-0 py-1.5 px-3"
                      style={{ fontSize: '0.85rem' }}
                      placeholder="변경할 닉네임"
                      value={newNickname}
                      onChange={(e) => setNewNickname(e.target.value)}
                      autoFocus
                    />
                    <button type="submit" className="onboarding-btn w-auto py-1.5 px-3" style={{ fontSize: '0.82rem', whiteSpace: 'nowrap' }}>변경</button>
                    <button 
                      type="button" 
                      onClick={() => setIsEditingNickname(false)} 
                      className="btn btn-sm btn-light border py-1.5 px-2.5 rounded-3" 
                      style={{ fontSize: '0.82rem', whiteSpace: 'nowrap', borderRadius: '14px' }}
                    >
                      취소
                    </button>
                  </form>
                </div>
              )}

              {/* 접속자 목록 패널 */}
              {showUserList && connectionStatus === 'connected' && (
                <div className="chat-active-users-panel">
                  <div className="active-users-panel-header">접속자 목록</div>
                  <div className="active-users-list">
                    {activeUsers.map((user, idx) => {
                      const isCurrentUser = user === nickname;
                      const avatarColor = getAvatarColor(user);
                      return (
                        <div key={idx} className="active-user-item">
                          <span className="active-user-dot"></span>
                          <span 
                            className="active-user-avatar"
                            style={{ backgroundColor: avatarColor.bg, color: avatarColor.text }}
                          >
                            {user.charAt(0)}
                          </span>
                          <span className="active-user-name">
                            {user} {isCurrentUser && <span className="active-user-me-tag">(나)</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 메시지 영역 */}
              <div className="chat-messages-area">
                {connectionStatus === 'disconnected' && (
                  <div className="system-msg-container">
                    <span className="system-msg-bubble bg-danger-subtle text-danger text-center" style={{ fontSize: '0.72rem', lineHeight: 1.4 }}>
                      ⚠️ 채팅 서버와 연결되지 않았습니다.<br />
                      Nginx 등 역방향 프록시의 WebSocket 설정(Upgrade 헤더)을 확인해 주세요.
                    </span>
                  </div>
                )}
                {messages.map((msg, index) => {
                  if (msg.type === 'system') {
                    return (
                      <div key={index} className="system-msg-container">
                        <span className="system-msg-bubble">{msg.text}</span>
                      </div>
                    );
                  }

                  const isMe = msg.sender === nickname;
                  const isImage = msg.fileUrl && /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(msg.fileUrl);
                  const avatarColor = getAvatarColor(msg.sender);

                  return (
                    <div key={msg.id || index} className={`chat-bubble-row ${isMe ? 'me' : ''}`}>
                      {!isMe && (
                        <div 
                          className="chat-bubble-avatar" 
                          style={{ backgroundColor: avatarColor.bg, color: avatarColor.text }}
                        >
                          {msg.sender.charAt(0)}
                        </div>
                      )}
                      <div className="chat-bubble-content-box">
                        {!isMe && <span className="chat-bubble-sender">{msg.sender}</span>}
                        <div className="chat-bubble-body">
                          {msg.text && <div style={{ marginBottom: msg.fileUrl ? '6px' : '0' }}>{msg.text}</div>}
                          {msg.fileUrl && (
                            isImage ? (
                              <img
                                src={msg.fileUrl}
                                alt={msg.fileName}
                                className="chat-image-thumbnail"
                                onClick={() => setPreviewImage(msg.fileUrl)}
                              />
                            ) : (
                              <a
                                href={msg.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="chat-file-card"
                                title="새 창에서 다운로드"
                              >
                                <span style={{ marginRight: '4px' }}>📁</span>
                                <span className="text-truncate" style={{ maxWidth: '140px' }}>{msg.fileName}</span>
                                <FaExternalLinkAlt size={10} style={{ marginLeft: '4px', opacity: 0.7 }} />
                              </a>
                            )
                          )}
                        </div>
                      </div>
                      <span className="chat-bubble-time">{msg.time}</span>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* 푸터 / 입력창 */}
              <div className="chat-window-footer">
                {isUploading && (
                  <div className="chat-file-uploader">
                    <span className="file-uploader-spinner"></span>
                    <span>파일 업로드 중...</span>
                  </div>
                )}

                <div className="chat-input-controls-row">
                  {/* 파일 업로드 트리거 */}
                  <button className="chat-icon-btn" onClick={() => fileInputRef.current.click()} title="파일 첨부">
                    <FaPaperclip size={15} />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    style={{ display: 'none' }}
                  />

                  {/* 텍스트 인풋창 */}
                  <form onSubmit={handleSendMessage} className="chat-input-box-container">
                    <input
                      type="text"
                      className="chat-text-input"
                      placeholder="메시지를 입력하세요..."
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                    />
                    
                    {/* 이모티콘 트리거 */}
                    <button
                      type="button"
                      className="chat-icon-btn"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      title="이모티콘"
                      style={{ color: showEmojiPicker ? '#6366f1' : '#64748b' }}
                    >
                      <FaSmile size={16} />
                    </button>
                  </form>

                  {/* 전송 버튼 */}
                  <button className="chat-send-btn" onClick={handleSendMessage} title="보내기">
                    <FaPaperPlane size={12} />
                  </button>
                </div>

                {/* 이모티콘 팝오버 */}
                {showEmojiPicker && (
                  <div className="emoji-selector-popover">
                    {RECOMMENDED_EMOJIS.map((emoji) => (
                      <button
                        key={emoji}
                        type="button"
                        className="emoji-btn-item"
                        onClick={() => handleSelectEmoji(emoji)}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {/* 3. 이미지 확대 뷰 모달 */}
      {previewImage && (
        <div className="chat-image-overlay" onClick={() => setPreviewImage(null)}>
          <div className="chat-image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="chat-image-modal-close" onClick={() => setPreviewImage(null)}>✖</button>
            <img src={previewImage} className="chat-image-modal-img" alt="Zoomed chat media" />
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingChat;
